import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playSfx, playJingle } from "@/lib/sound";
import { Coins, Star, RotateCcw, ArrowLeft, Trophy, Send, Skull, TriangleAlert } from "lucide-react";
import AdInterstitial from "@/components/AdInterstitial";

const GAME_META = {
  astro_dodge: { name: "Astro Dodge", accent: "hsl(var(--primary))" },
  bolt_catch: { name: "Bolt Catch", accent: "hsl(var(--secondary))" },
  memory_core: { name: "Memory Core", accent: "hsl(var(--accent))" },
  rhythm_hacker: { name: "Rhythm Hacker", accent: "hsl(var(--secondary))" },
  bot_racer: { name: "Bot Racer", accent: "hsl(var(--primary))" },
  circuit_snake: { name: "Circuit Snake", accent: "hsl(var(--accent))" },
};

/* ------------- Canvas helper games ------------- */
function useCanvasGame(draw) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const cleanup = draw(canvas);
    return cleanup;
  }, [draw]);
  return ref;
}

const imgReady = (im) => im && im.complete && im.naturalWidth > 0;
function useImages(map) {
  const ref = useRef(null);
  if (!ref.current) {
    const out = {};
    for (const k in map) { const i = new Image(); i.src = map[k]; out[k] = i; }
    ref.current = out;
  }
  return ref.current;
}
const S = (n) => `/assets/sprites/${n}.png`;

function AstroDodge() {
  const navigate = useNavigate();
  const { patchUser } = useAuth();
  const [score, setScore] = useState(0);
  const [shields, setShields] = useState(3);
  const [over, setOver] = useState(false);
  const [warning, setWarning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [runKey, setRunKey] = useState(0);
  const scoreRef = useRef(0);
  const shieldsRef = useRef(3);
  const warnTimer = useRef(null);
  const MAX_SHIELDS = 3;
  const imgsRef = useRef(null);
  if (!imgsRef.current) {
    const mk = (src) => { const i = new Image(); i.src = src; return i; };
    imgsRef.current = {
      player: mk("/assets/sprites/orbo_ship.png"),
      small: mk("/assets/sprites/small_asteroid.png"),
      asteroid: mk("/assets/sprites/heavy_asteroid.png"),
      supernova: mk("/assets/sprites/blue_supernova.png"),
      bg: mk("/assets/sprites/galaxy_background.png"),
      nebGreen: mk("/assets/sprites/green_nebula.png"),
      nebRed: mk("/assets/sprites/red_nebula.png"),
    };
  }

  // hazard archetypes: distinct art, speed, hitbox and effect
  const VARIANTS = {
    small: { size: 30, speed: 6.6, dmg: 1, wave: false, hit: 0.40 },      // fast small asteroid, chip 1 shield
    asteroid: { size: 94, speed: 1.7, dmg: 99, wave: false, hit: 0.44 },  // slow, huge, instant kill
    supernova: { size: 48, speed: 3.0, dmg: -1, wave: true, hit: 0.46 },  // rare, sine drift, HEALS 1 shield
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/games/score", { game: "astro_dodge", score: scoreRef.current });
      patchUser({ credits: data.total_credits });
      playSfx("win"); playJingle(localStorage.getItem("activePetType"));
      toast.success(`+${data.credits_earned} credits earned!`);
    } catch { toast.error("Could not submit score."); }
    navigate("/app/arcade");
  };

  const retry = () => {
    scoreRef.current = 0; shieldsRef.current = MAX_SHIELDS;
    setScore(0); setShields(MAX_SHIELDS); setOver(false);
    setRunKey((k) => k + 1); // re-runs the canvas loop from scratch, no backend call
  };

  const draw = useCallback((canvas) => {
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const IMG = imgsRef.current;
    let raf, running = true, t = 0, spawn = 0, speed = 2.6, bgY = 0, inv = 0;
    scoreRef.current = 0; shieldsRef.current = MAX_SHIELDS;
    const player = { x: W / 2, y: H - 62, width: 46, height: 46 };
    let hazards = [];

    const ready = (im) => im && im.complete && im.naturalWidth > 0;
    let particles = [];  // supernova pickup sparkle burst

    const burst = (x, y) => {
      for (let i = 0; i < 18; i++) {
        const a = (Math.PI * 2 * i) / 18 + Math.random() * 0.3;
        const sp = 2 + Math.random() * 3.5;
        particles.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 1, r: 2 + Math.random() * 3 });
      }
    };
    const triggerWarn = () => {
      setWarning(true);
      if (warnTimer.current) clearTimeout(warnTimer.current);
      warnTimer.current = setTimeout(() => setWarning(false), 1600);
    };

    // two drifting nebula clouds (40-60% width): green slows asteroids, red speeds them up
    const mkNeb = (type) => {
      const w = W * (0.4 + Math.random() * 0.2);
      const h = W * (0.5 + Math.random() * 0.35);
      return { type, w, h, x: Math.random() * (W - w), y: -h - Math.random() * H, vy: 0.35 + Math.random() * 0.5, vx: (Math.random() - 0.5) * 0.7, warned: false };
    };
    let nebulas = [mkNeb("green"), mkNeb("red")];

    const move = (clientX) => {
      const rect = canvas.getBoundingClientRect();
      const half = player.width / 2;
      player.x = Math.max(half, Math.min(W - half, (clientX - rect.left) * (W / rect.width)));
    };
    const mm = (e) => move(e.clientX);
    const tm = (e) => { if (e.touches[0]) move(e.touches[0].clientX); };
    canvas.addEventListener("mousemove", mm);
    canvas.addEventListener("touchmove", tm, { passive: true });

    const spawnHazard = () => {
      // supernova ~1/10th of the old plasma rate (was ~0.24 -> ~0.025)
      const r = Math.random();
      const type = r < 0.025 ? "supernova" : r < 0.205 ? "asteroid" : "small";
      const v = VARIANTS[type];
      const bx = v.size / 2 + Math.random() * (W - v.size);
      hazards.push({ type, x: bx, baseX: bx, y: -v.size, ...v, phase: Math.random() * Math.PI * 2 });
    };

    const endGame = () => { running = false; cancelAnimationFrame(raf); if (warnTimer.current) clearTimeout(warnTimer.current); setWarning(false); setOver(true); playSfx("lose"); };

    const nebFactor = (hx, hy) => {
      let f = 1;
      for (const n of nebulas) {
        if (hx > n.x && hx < n.x + n.w && hy > n.y && hy < n.y + n.h) f *= n.type === "green" ? 0.45 : 1.85;
      }
      return f;
    };

    const loop = () => {
      if (!running) return;
      t++; spawn++;

      // moving starfield background
      if (ready(IMG.bg)) {
        bgY = (bgY + speed * 0.5) % H;
        ctx.drawImage(IMG.bg, 0, bgY - H, W, H);
        ctx.drawImage(IMG.bg, 0, bgY, W, H);
      } else {
        ctx.fillStyle = "#05060a"; ctx.fillRect(0, 0, W, H);
      }

      // drift + draw nebula clouds (environment layer)
      nebulas.forEach((n) => {
        n.y += n.vy; n.x += n.vx;
        if (n.x < -n.w * 0.5 || n.x > W - n.w * 0.5) n.vx *= -1;
        if (n.type === "red" && !n.warned && n.y > -n.h * 0.35) { n.warned = true; triggerWarn(); }
        if (n.y > H) Object.assign(n, mkNeb(n.type));
      });
      ctx.globalAlpha = 0.5;
      nebulas.forEach((n) => {
        const im = n.type === "green" ? IMG.nebGreen : IMG.nebRed;
        if (ready(im)) ctx.drawImage(im, n.x, n.y, n.w, n.h);
      });
      ctx.globalAlpha = 1;

      if (spawn > Math.max(14, 38 - t / 120)) { spawn = 0; spawnHazard(); }
      speed += 0.0008;

      hazards.forEach((hz) => {
        const drift = hz.type === "asteroid" ? 0 : speed * 0.18;
        hz.y += (hz.speed + drift) * nebFactor(hz.x, hz.y);
        if (hz.wave) hz.x = hz.baseX + Math.sin(hz.y / 34 + hz.phase) * 52;
      });
      hazards = hazards.filter((hz) => hz.y < H + hz.size);

      // draw hazards by variant art
      hazards.forEach((hz) => {
        const im = IMG[hz.type];
        if (hz.type === "small") { ctx.save(); ctx.shadowColor = "#00ffff"; ctx.shadowBlur = 18; }
        if (ready(im)) ctx.drawImage(im, hz.x - hz.size / 2, hz.y - hz.size / 2, hz.size, hz.size);
        else { ctx.beginPath(); ctx.arc(hz.x, hz.y, hz.size / 2, 0, 7); ctx.fillStyle = hz.type === "supernova" ? "#2D9CFF" : hz.type === "small" ? "#00ffff" : "#ff5e57"; ctx.fill(); }
        if (hz.type === "small") ctx.restore();
      });

      // player (flicker while invulnerable)
      const flicker = inv > 0 && Math.floor(inv / 4) % 2 === 0;
      if (!flicker) {
        if (ready(IMG.player)) ctx.drawImage(IMG.player, player.x - player.width / 2, player.y - player.height / 2, player.width, player.height);
        else { ctx.beginPath(); ctx.arc(player.x, player.y, player.width / 2, 0, 7); ctx.fillStyle = "#FFB300"; ctx.fill(); }
      }

      // on-canvas shield pips
      for (let i = 0; i < MAX_SHIELDS; i++) {
        ctx.beginPath(); ctx.arc(16 + i * 18, 18, 6, 0, 7);
        ctx.fillStyle = i < shieldsRef.current ? "#FFB300" : "rgba(255,255,255,0.15)";
        ctx.fill();
      }

      // supernova sparkle particles
      if (particles.length) {
        ctx.globalCompositeOperation = "lighter";
        particles.forEach((p) => {
          p.x += p.vx; p.y += p.vy; p.vx *= 0.94; p.vy *= 0.94; p.life -= 0.035;
          ctx.globalAlpha = Math.max(0, p.life);
          ctx.shadowColor = "#5cc8ff"; ctx.shadowBlur = 12;
          ctx.beginPath(); ctx.arc(p.x, p.y, p.r * p.life, 0, 7);
          ctx.fillStyle = "#bfe9ff"; ctx.fill();
        });
        ctx.shadowBlur = 0; ctx.globalAlpha = 1; ctx.globalCompositeOperation = "source-over";
        particles = particles.filter((p) => p.life > 0);
      }

      // collisions
      for (const hz of hazards) {
        const d = Math.hypot(hz.x - player.x, hz.y - player.y);
        if (d >= hz.size * hz.hit + player.width * 0.40) continue;
        if (hz.type === "supernova") {                       // heal (works even during invuln)
          if (shieldsRef.current < MAX_SHIELDS) { shieldsRef.current += 1; setShields(shieldsRef.current); }
          burst(player.x, player.y);                          // sparkle burst on pickup
          hz.y = H + 999; playSfx("pop");
          continue;
        }
        if (inv > 0) continue;
        if (hz.dmg >= 99) { endGame(); return; }              // heavy asteroid = instant kill
        shieldsRef.current -= hz.dmg;
        setShields(Math.max(0, shieldsRef.current));
        hz.y = H + 999; inv = 48; playSfx("pop");
        if (shieldsRef.current <= 0) { endGame(); return; }
        break;
      }
      if (inv > 0) inv--;

      if (t % 6 === 0) { scoreRef.current += 1; setScore(scoreRef.current); }
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { running = false; cancelAnimationFrame(raf); if (warnTimer.current) clearTimeout(warnTimer.current); canvas.removeEventListener("mousemove", mm); canvas.removeEventListener("touchmove", tm); };
  }, [runKey]);

  const ref = useCanvasGame(draw);
  return (
    <div className="relative">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <span className="label-cap">Shields</span>
          <span className="flex gap-1" data-testid="shield-pips">
            {[0, 1, 2].map((i) => (
              <span key={i} className={`w-3 h-3 rounded-full ${i < shields ? "bg-primary" : "bg-muted"}`} />
            ))}
          </span>
        </div>
        <div><span className="label-cap mr-2">Score</span><span className="font-head font-black text-2xl text-primary">{score}</span></div>
      </div>
      <div className="relative">
        <canvas ref={ref} width={520} height={520} data-testid="game-canvas" className="w-full rounded-2xl dark:rounded-none border border-border bg-black touch-none cursor-crosshair" />
        {warning && !over && (
          <div data-testid="nebula-warning" className="absolute top-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
            <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-red-600/90 border border-red-300 text-white font-head font-black text-xs sm:text-sm tracking-wider uppercase shadow-lg animate-pulse">
              <TriangleAlert className="w-4 h-4" /> Danger: Radiation Cloud Detected
            </div>
          </div>
        )}
        {over && (
          <div data-testid="astro-gameover" className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/80 backdrop-blur-sm rounded-2xl dark:rounded-none p-6 text-center">
            <Skull className="w-12 h-12 text-destructive" />
            <div>
              <div className="label-cap">Game Over</div>
              <div className="font-head text-5xl font-black text-primary neon-text">{score}</div>
            </div>
            <button data-testid="submit-score-btn" onClick={submit} disabled={submitting} className="btn-primary w-64 justify-center">
              <Send className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit Score to Earn Credits"}
            </button>
            <button data-testid="retry-btn" onClick={retry} disabled={submitting} className="btn-ghost w-64 justify-center">
              <RotateCcw className="w-4 h-4" /> Retry Without Submitting
            </button>
          </div>
        )}
      </div>
      <p className="text-center text-sm text-muted-foreground mt-3">Dodge <span className="text-primary">asteroids</span> (small = −1 shield, <span className="text-destructive">heavy = instant death</span>). Grab a rare <span className="text-blue-400">blue supernova</span> to restore a shield. <span className="text-green-400">Green</span> clouds slow rocks, <span className="text-red-400">red</span> clouds speed them up.</p>
    </div>
  );
}

function BoltCatch({ onEnd }) {
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const IMG = useImages({ hand: S("bolt_hand"), cell: S("energy_cell"), scrap: S("scrap_cell"), bg: S("lab_background") });
  const draw = useCallback((canvas) => {
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    let raf, running = true, t = 0, spawn = 0, timeLeft = 30 * 60; // 30s at 60fps
    const HAND_W = 92, HAND_H = 92;
    const tray = { x: W / 2, w: 76, catchY: H - 58 };
    let drops = [];
    const move = (clientX) => {
      const rect = canvas.getBoundingClientRect();
      tray.x = Math.max(tray.w / 2, Math.min(W - tray.w / 2, (clientX - rect.left) * (W / rect.width)));
    };
    const mm = (e) => move(e.clientX);
    const tm = (e) => { if (e.touches[0]) move(e.touches[0].clientX); };
    canvas.addEventListener("mousemove", mm);
    canvas.addEventListener("touchmove", tm, { passive: true });

    const loop = () => {
      if (!running) return;
      t++; spawn++; timeLeft--;
      if (timeLeft <= 0) { running = false; onEnd(scoreRef.current); return; }
      if (imgReady(IMG.bg)) ctx.drawImage(IMG.bg, 0, 0, W, H);
      else { ctx.fillStyle = "#0a0710"; ctx.fillRect(0, 0, W, H); }
      ctx.fillStyle = "rgba(5,6,12,0.35)"; ctx.fillRect(0, 0, W, H); // darken bg for contrast

      if (spawn > 26) { spawn = 0; drops.push({ x: 26 + Math.random() * (W - 52), y: -20, bad: Math.random() < 0.28, v: 2.4 + Math.random() * 2, r: 22 }); }
      drops.forEach((d) => { d.y += d.v; });

      drops.forEach((d) => {
        const im = d.bad ? IMG.scrap : IMG.cell;
        if (imgReady(im)) {
          if (!d.bad) { ctx.shadowColor = "#ffd32a"; ctx.shadowBlur = 16; }
          ctx.drawImage(im, d.x - d.r, d.y - d.r, d.r * 2, d.r * 2); ctx.shadowBlur = 0;
        } else { ctx.beginPath(); ctx.arc(d.x, d.y, 12, 0, 7); ctx.fillStyle = d.bad ? "#7a5230" : "#ffd32a"; ctx.fill(); }
      });

      // robot hand catcher
      if (imgReady(IMG.hand)) ctx.drawImage(IMG.hand, tray.x - HAND_W / 2, H - HAND_H, HAND_W, HAND_H);
      else { ctx.fillStyle = "#FFB300"; ctx.fillRect(tray.x - tray.w / 2, H - 40, tray.w, 16); }

      drops = drops.filter((d) => {
        if (d.y > tray.catchY - 22 && d.y < tray.catchY + 22 && Math.abs(d.x - tray.x) < tray.w / 2 + 14) {
          scoreRef.current += d.bad ? -5 : 10;
          if (scoreRef.current < 0) scoreRef.current = 0;
          setScore(scoreRef.current);
          return false;
        }
        return d.y < H + 24;
      });

      ctx.fillStyle = "#fff"; ctx.font = "bold 16px sans-serif";
      ctx.fillText(`${Math.ceil(timeLeft / 60)}s`, 14, 26);
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { running = false; cancelAnimationFrame(raf); canvas.removeEventListener("mousemove", mm); canvas.removeEventListener("touchmove", tm); };
  }, [onEnd, IMG]);
  const ref = useCanvasGame(draw);
  return (
    <div>
      <div className="flex justify-between mb-3"><span className="label-cap">Score</span><span className="font-head font-black text-2xl text-secondary">{score}</span></div>
      <canvas ref={ref} width={520} height={520} data-testid="game-canvas" className="w-full rounded-2xl dark:rounded-none border border-border bg-black touch-none cursor-crosshair" />
      <p className="text-center text-sm text-muted-foreground mt-3">Catch glowing <span className="text-secondary">energy cells</span> (+10), dodge <span className="text-orange-400">rusty scrap</span> (−5). 30 seconds!</p>
    </div>
  );
}

const FACTIONS = ["logic", "rust", "holo", "solar", "void", "pulse", "titan", "flux"];
const FACTION_NAMES = { logic: "Logic Core", rust: "Rust Syndicate", holo: "Holo Collective", solar: "Solar Vanguard", void: "Void Order", pulse: "Pulse Circuit", titan: "Titan Guard", flux: "Flux Union" };
function MemoryCore({ onEnd }) {
  const [cards, setCards] = useState(() => {
    const deck = [...FACTIONS, ...FACTIONS].map((v, i) => ({ id: i, v, flipped: false, matched: false }));
    for (let i = deck.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [deck[i], deck[j]] = [deck[j], deck[i]]; }
    return deck;
  });
  const [flipped, setFlipped] = useState([]);
  const [moves, setMoves] = useState(0);
  const [lock, setLock] = useState(false);

  const flip = (idx) => {
    if (lock || cards[idx].flipped || cards[idx].matched) return;
    const nc = cards.map((c, i) => (i === idx ? { ...c, flipped: true } : c));
    setCards(nc);
    const nf = [...flipped, idx];
    setFlipped(nf);
    if (nf.length === 2) {
      setMoves((m) => m + 1); setLock(true);
      const [a, b] = nf;
      if (nc[a].v === nc[b].v) {
        setTimeout(() => {
          setCards((cs) => cs.map((c, i) => (i === a || i === b ? { ...c, matched: true } : c)));
          setFlipped([]); setLock(false);
        }, 350);
      } else {
        setTimeout(() => {
          setCards((cs) => cs.map((c, i) => (i === a || i === b ? { ...c, flipped: false } : c)));
          setFlipped([]); setLock(false);
        }, 750);
      }
    }
  };

  useEffect(() => {
    if (cards.every((c) => c.matched)) {
      const score = Math.max(20, 200 - moves * 8);
      setTimeout(() => onEnd(score), 500);
    }
  }, [cards, moves, onEnd]);

  return (
    <div>
      <div className="flex justify-between mb-3"><span className="label-cap">Moves</span><span className="font-head font-black text-2xl text-accent">{moves}</span></div>
      <div className="grid grid-cols-4 gap-3" data-testid="memory-grid">
        {cards.map((c, i) => {
          const shown = c.flipped || c.matched;
          return (
            <button key={c.id} data-testid={`memory-card-${i}`} onClick={() => flip(i)} title={shown ? FACTION_NAMES[c.v] : "Facedown chip"}
              className={`aspect-square rounded-2xl dark:rounded-none overflow-hidden grid place-items-center transition-transform duration-200 border ${
                shown ? "bg-black border-primary" : "border-transparent hover:scale-95"
              } ${c.matched ? "opacity-60 ring-1 ring-primary" : ""}`}>
              <img src={shown ? S(`faction_${c.v}`) : S("chip_back")} alt={shown ? FACTION_NAMES[c.v] : "chip"}
                className="w-full h-full object-contain p-1" draggable={false} />
            </button>
          );
        })}
      </div>
      <p className="text-center text-sm text-muted-foreground mt-3">Flip the holographic chips and match all <span className="text-primary">faction logos</span> in as few moves as possible.</p>
    </div>
  );
}

/* ------------- Rhythm Hacker ------------- */
const LANE_KEYS = ["a", "s", "d", "f"];
const LANE_COLORS = ["#FFB300", "#ffe27a", "#ff5e57", "#b14dff"];
function RhythmHacker({ onEnd }) {
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const scoreRef = useRef(0), comboRef = useRef(0);
  const [flash, setFlash] = useState([-1, -1, -1, -1]);
  const notesRef = useRef([]);
  const hitRef = useRef(null);
  const IMG = useImages({ pulse: S("laser_pulse"), bg: S("rhythm_bg") });

  const draw = useCallback((canvas) => {
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const laneW = W / 4, hitY = H - 70, hitH = 44;
    let raf, running = true, t = 0, spawn = 0, timeLeft = 32 * 60;
    let hitPulse = 0;
    notesRef.current = [];

    const tryHit = (lane) => {
      const notes = notesRef.current;
      let idx = -1, bestDist = 1e9;
      notes.forEach((n, i) => {
        if (n.lane === lane) { const d = Math.abs(n.y - hitY); if (d < bestDist) { bestDist = d; idx = i; } }
      });
      if (idx >= 0 && bestDist < hitH) {
        const perfect = bestDist < 16;
        notes.splice(idx, 1);
        comboRef.current += 1;
        scoreRef.current += (perfect ? 15 : 8) + Math.floor(comboRef.current / 5) * 2;
        setScore(scoreRef.current); setCombo(comboRef.current);
        setFlash((f) => { const nf = [...f]; nf[lane] = t; return nf; });
        hitPulse = 10;
      } else {
        comboRef.current = 0; setCombo(0);
      }
    };
    hitRef.current = tryHit;

    const onKey = (e) => { const l = LANE_KEYS.indexOf(e.key.toLowerCase()); if (l >= 0) { e.preventDefault(); tryHit(l); } };
    const onClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) * (W / rect.width);
      tryHit(Math.floor(x / laneW));
    };
    window.addEventListener("keydown", onKey);
    canvas.addEventListener("pointerdown", onClick);

    const loop = () => {
      if (!running) return;
      t++; spawn++; timeLeft--;
      if (timeLeft <= 0) { running = false; onEnd(scoreRef.current); return; }
      if (imgReady(IMG.bg)) ctx.drawImage(IMG.bg, 0, 0, W, H);
      else { ctx.fillStyle = "#070510"; ctx.fillRect(0, 0, W, H); }
      ctx.fillStyle = "rgba(7,5,16,0.45)"; ctx.fillRect(0, 0, W, H);

      // lane dividers
      for (let i = 1; i < 4; i++) { ctx.strokeStyle = "rgba(255,255,255,0.08)"; ctx.beginPath(); ctx.moveTo(i * laneW, 0); ctx.lineTo(i * laneW, H); ctx.stroke(); }

      // glowing alignment bar
      ctx.save();
      ctx.shadowColor = "#FFB300"; ctx.shadowBlur = 18 + hitPulse * 2;
      ctx.fillStyle = `rgba(255,179,0,${0.22 + hitPulse * 0.04})`;
      ctx.fillRect(0, hitY, W, hitH);
      ctx.strokeStyle = "#FFB300"; ctx.lineWidth = 3; ctx.strokeRect(1, hitY, W - 2, hitH);
      ctx.restore();
      if (hitPulse > 0) hitPulse--;

      // per-lane hit-zone flash
      for (let i = 0; i < 4; i++) {
        const fl = flash[i] != null && t - flash[i] < 8;
        if (fl) { ctx.fillStyle = LANE_COLORS[i]; ctx.globalAlpha = 0.35 * (1 - (t - flash[i]) / 8); ctx.fillRect(i * laneW, hitY, laneW, hitH); ctx.globalAlpha = 1; }
      }

      if (spawn > (26 - Math.min(10, t / 200))) { spawn = 0; notesRef.current.push({ lane: Math.floor(Math.random() * 4), y: -30 }); }
      const speed = (3.4 + t / 1200) * 0.25 * (1 + 0.15 * Math.floor(scoreRef.current / 250));
      notesRef.current.forEach((n) => { n.y += speed; });
      notesRef.current = notesRef.current.filter((n) => {
        if (n.y > H + 20) { comboRef.current = 0; setCombo(0); return false; }
        return true;
      });

      // laser pulse notes (per-lane coloured glow)
      const ph = 30, pw = laneW - 16;
      notesRef.current.forEach((n) => {
        const x = n.lane * laneW + laneW / 2;
        ctx.save();
        ctx.shadowColor = LANE_COLORS[n.lane]; ctx.shadowBlur = 18;
        if (imgReady(IMG.pulse)) ctx.drawImage(IMG.pulse, x - pw / 2, n.y - ph / 2, pw, ph);
        else { ctx.fillStyle = LANE_COLORS[n.lane]; ctx.beginPath(); ctx.roundRect(x - pw / 2, n.y - ph / 2, pw, ph, 8); ctx.fill(); }
        ctx.restore();
      });
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { running = false; cancelAnimationFrame(raf); window.removeEventListener("keydown", onKey); canvas.removeEventListener("pointerdown", onClick); };
  }, [onEnd, flash, IMG]);
  const ref = useCanvasGame(draw);
  return (
    <div>
      <div className="flex justify-between mb-3">
        <span className="label-cap">Combo <span className="text-secondary font-black text-lg">x{combo}</span></span>
        <span className="font-head font-black text-2xl text-secondary">{score}</span>
      </div>
      <canvas ref={ref} width={520} height={560} data-testid="game-canvas" className="w-full rounded-2xl dark:rounded-none border border-border bg-black touch-none select-none" />
      <div className="grid grid-cols-4 gap-2 mt-3">
        {LANE_KEYS.map((k, i) => (
          <button key={k} data-testid={`rhythm-lane-${i}`} onPointerDown={() => hitRef.current?.(i)}
            className="py-3 rounded-xl dark:rounded-none font-black uppercase border" style={{ borderColor: LANE_COLORS[i], color: LANE_COLORS[i] }}>{k}</button>
        ))}
      </div>
      <p className="text-center text-sm text-muted-foreground mt-3">Tap A S D F (or the buttons) as the <span className="text-secondary">laser pulses</span> hit the glowing alignment bar. 32 seconds!</p>
    </div>
  );
}

/* ------------- Bot Racer ------------- */
function BotRacer({ onEnd }) {
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const IMG = useImages({ car: S("racer_car"), scrap: S("scrap_car"), bg: S("highway_background") });
  const draw = useCallback((canvas) => {
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    let raf, running = true, t = 0, spawn = 0, speed = 1.15, bgY = 0;
    const car = { x: W / 2, w: 44, h: 68 };
    let cars = [];
    const move = (clientX) => { const rect = canvas.getBoundingClientRect(); car.x = Math.max(70, Math.min(W - 70, (clientX - rect.left) * (W / rect.width))); };
    const mm = (e) => move(e.clientX);
    const tm = (e) => { if (e.touches[0]) move(e.touches[0].clientX); };
    const onKey = (e) => { if (e.key === "ArrowLeft") car.x = Math.max(70, car.x - 40); if (e.key === "ArrowRight") car.x = Math.min(W - 70, car.x + 40); };
    canvas.addEventListener("mousemove", mm); canvas.addEventListener("touchmove", tm, { passive: true }); window.addEventListener("keydown", onKey);

    const loop = () => {
      if (!running) return;
      t++; spawn++; speed += 0.0008;
      const sv = speed * (1 + scoreRef.current / 500);  // scale difficulty with distance
      // scrolling neon highway background (seamless tiling)
      if (imgReady(IMG.bg)) {
        bgY = (bgY + sv) % H;
        const y = Math.floor(bgY);
        ctx.drawImage(IMG.bg, 0, y - H, W, H + 2);
        ctx.drawImage(IMG.bg, 0, y, W, H + 2);
      } else { ctx.fillStyle = "#0a0e14"; ctx.fillRect(0, 0, W, H); }

      if (spawn > Math.max(26, 60 - t / 60)) { spawn = 0; cars.push({ x: 80 + Math.random() * (W - 160), y: -70 }); }
      cars.forEach((c) => { c.y += sv; });
      cars = cars.filter((c) => c.y < H + 70);
      cars.forEach((c) => {
        if (imgReady(IMG.scrap)) { ctx.save(); ctx.shadowColor = "#ff8c00"; ctx.shadowBlur = 14; ctx.drawImage(IMG.scrap, c.x - 26, c.y, 52, 68); ctx.restore(); }
        else { ctx.fillStyle = "#ff3b5c"; ctx.beginPath(); ctx.roundRect(c.x - 17, c.y, 34, 54, 8); ctx.fill(); }
        // neon-orange highlight border
        ctx.save(); ctx.strokeStyle = "#ff8c00"; ctx.lineWidth = 2.5; ctx.shadowColor = "#ff8c00"; ctx.shadowBlur = 10;
        ctx.beginPath(); ctx.roundRect(c.x - 26, c.y, 52, 68, 10); ctx.stroke(); ctx.restore();
      });
      // player vehicle
      if (imgReady(IMG.car)) { ctx.save(); ctx.shadowColor = "#FFB300"; ctx.shadowBlur = 16; ctx.drawImage(IMG.car, car.x - car.w / 2, H - 92, car.w, car.h); ctx.restore(); }
      else { ctx.fillStyle = "#FFB300"; ctx.beginPath(); ctx.roundRect(car.x - car.w / 2, H - 80, car.w, car.h, 8); ctx.fill(); }

      for (const c of cars) {
        if (Math.abs(c.x - car.x) < 34 && c.y + 60 > H - 92 && c.y < H - 30) { running = false; onEnd(scoreRef.current); return; }
      }
      if (t % 4 === 0) { scoreRef.current += 1; setScore(scoreRef.current); }
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { running = false; cancelAnimationFrame(raf); canvas.removeEventListener("mousemove", mm); canvas.removeEventListener("touchmove", tm); window.removeEventListener("keydown", onKey); };
  }, [onEnd, IMG]);
  const ref = useCanvasGame(draw);
  return (
    <div>
      <div className="flex justify-between mb-3"><span className="label-cap">Distance</span><span className="font-head font-black text-2xl text-primary">{score}m</span></div>
      <canvas ref={ref} width={520} height={560} data-testid="game-canvas" className="w-full rounded-2xl dark:rounded-none border border-border bg-black touch-none cursor-crosshair" />
      <p className="text-center text-sm text-muted-foreground mt-3">Mouse / arrow keys to weave through the <span className="text-destructive">scrap traffic</span> on the neon highway. Don't crash!</p>
    </div>
  );
}

/* ------------- Circuit Snake ------------- */
function CircuitSnake({ onEnd }) {
  const [score, setScore] = useState(0);
  const dirRef = useRef({ x: 1, y: 0 });
  const setDir = (x, y) => { const d = dirRef.current; if (d.x === -x && d.y === -y) return; dirRef.current = { x, y }; };
  const IMG = useImages({ head: S("worm_head"), body: S("worm_body"), food: S("microchip"), bg: S("circuit_bg") });
  const draw = useCallback((canvas) => {
    const ctx = canvas.getContext("2d");
    const G = 20, N = canvas.width / G;
    let raf, running = true, frame = 0, sc = 0;
    let snake = [{ x: 8, y: 12 }, { x: 7, y: 12 }, { x: 6, y: 12 }];
    let food = { x: 14, y: 12 };
    const rndFood = () => { food = { x: Math.floor(Math.random() * N), y: Math.floor(Math.random() * N) }; };
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if (k === "arrowup" || k === "w") setDir(0, -1);
      else if (k === "arrowdown" || k === "s") setDir(0, 1);
      else if (k === "arrowleft" || k === "a") setDir(-1, 0);
      else if (k === "arrowright" || k === "d") setDir(1, 0);
    };
    window.addEventListener("keydown", onKey);

    const angleFor = (d) => d.x === 1 ? 0 : d.x === -1 ? Math.PI : d.y === -1 ? -Math.PI / 2 : Math.PI / 2;

    const render = () => {
      const Wd = canvas.width, Hd = canvas.height;
      if (imgReady(IMG.bg)) ctx.drawImage(IMG.bg, 0, 0, Wd, Hd);
      else { ctx.fillStyle = "#060a08"; ctx.fillRect(0, 0, Wd, Hd); }
      ctx.fillStyle = "rgba(4,8,6,0.4)"; ctx.fillRect(0, 0, Wd, Hd);

      // food microchip
      const fx = food.x * G + G / 2, fy = food.y * G + G / 2, fs = G + 8;
      if (imgReady(IMG.food)) { ctx.save(); ctx.shadowColor = "#3df5a0"; ctx.shadowBlur = 14; ctx.drawImage(IMG.food, fx - fs / 2, fy - fs / 2, fs, fs); ctx.restore(); }
      else { ctx.fillStyle = "#3df5a0"; ctx.beginPath(); ctx.arc(fx, fy, G / 2 - 3, 0, 7); ctx.fill(); }

      // body then head
      const bs = G + 6;
      for (let i = snake.length - 1; i >= 1; i--) {
        const s = snake[i], cx = s.x * G + G / 2, cy = s.y * G + G / 2;
        if (imgReady(IMG.body)) ctx.drawImage(IMG.body, cx - bs / 2, cy - bs / 2, bs, bs);
        else { ctx.fillStyle = "#1fae74"; ctx.beginPath(); ctx.roundRect(s.x * G + 2, s.y * G + 2, G - 4, G - 4, 5); ctx.fill(); }
      }
      const h = snake[0], hx = h.x * G + G / 2, hy = h.y * G + G / 2, hs = G + 10;
      ctx.save();
      ctx.shadowColor = "#3df5a0"; ctx.shadowBlur = 12;
      ctx.translate(hx, hy); ctx.rotate(angleFor(dirRef.current));
      if (imgReady(IMG.head)) ctx.drawImage(IMG.head, -hs / 2, -hs / 2, hs, hs);
      else { ctx.fillStyle = "#3df5a0"; ctx.beginPath(); ctx.arc(0, 0, G / 2, 0, 7); ctx.fill(); }
      ctx.restore();
    };

    const loop = () => {
      if (!running) return;
      raf = requestAnimationFrame(loop);
      frame++; if (frame % 7 !== 0) return; // ~8.5 moves/sec
      const d = dirRef.current;
      const head = { x: snake[0].x + d.x, y: snake[0].y + d.y };
      if (head.x < 0 || head.y < 0 || head.x >= N || head.y >= N || snake.some((s) => s.x === head.x && s.y === head.y)) {
        running = false; onEnd(sc); return;
      }
      snake.unshift(head);
      if (head.x === food.x && head.y === food.y) { sc += 10; setScore(sc); rndFood(); } else snake.pop();
      render();
    };
    render();
    loop();
    return () => { running = false; cancelAnimationFrame(raf); window.removeEventListener("keydown", onKey); };
  }, [onEnd, IMG]);
  const ref = useCanvasGame(draw);
  const dpad = (x, y, label, tid) => (
    <button data-testid={tid} onPointerDown={() => setDir(x, y)} className="w-14 h-14 rounded-xl dark:rounded-none border border-accent text-accent font-black grid place-items-center">{label}</button>
  );
  return (
    <div>
      <div className="flex justify-between mb-3"><span className="label-cap">Length</span><span className="font-head font-black text-2xl text-accent">{score}</span></div>
      <canvas ref={ref} width={520} height={520} data-testid="game-canvas" className="w-full rounded-2xl dark:rounded-none border border-border bg-black touch-none" />
      <div className="flex flex-col items-center gap-2 mt-3">
        {dpad(0, -1, "▲", "snake-up")}
        <div className="flex gap-2">{dpad(-1, 0, "◀", "snake-left")}{dpad(0, 1, "▼", "snake-down")}{dpad(1, 0, "▶", "snake-right")}</div>
      </div>
      <p className="text-center text-sm text-muted-foreground mt-3">Arrow keys / WASD (or the pad) to steer the <span className="text-accent">data-worm</span>. Absorb microchips, don't crash.</p>
    </div>
  );
}

/* ------------- shell ------------- */
export default function GamePlay() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { patchUser } = useAuth();
  const [phase, setPhase] = useState("intro"); // intro | playing | done
  const [result, setResult] = useState(null);
  const [key, setKey] = useState(0);
  const [adGate, setAdGate] = useState(true); // one interstitial before the game loads (per visit)
  const meta = GAME_META[gameId] || { name: "Game", accent: "hsl(var(--primary))" };

  const handleEnd = useCallback(async (score) => {
    setPhase("done");
    try {
      const { data } = await api.post("/games/score", { game: gameId, score });
      setResult({ score, ...data });
      patchUser({ credits: data.total_credits });
      playSfx("win");
      playJingle(localStorage.getItem("activePetType"));
      toast.success(`+${data.credits_earned} credits earned!`);
    } catch {
      setResult({ score, credits_earned: 0, xp_earned: 0 });
    }
  }, [gameId, patchUser]);

  const start = () => { setKey((k) => k + 1); setPhase("playing"); setResult(null); };

  const GameComp = { astro_dodge: AstroDodge, bolt_catch: BoltCatch, memory_core: MemoryCore, rhythm_hacker: RhythmHacker, bot_racer: BotRacer, circuit_snake: CircuitSnake }[gameId];
  useEffect(() => { if (!GameComp) navigate("/app/arcade"); }, [GameComp, navigate]);
  if (!GameComp) return null;

  if (adGate) return <AdInterstitial onDone={() => setAdGate(false)} label={`${meta.name} is loading…`} />;

  return (
    <div data-testid="gameplay-page" className="max-w-xl mx-auto">
      <button onClick={() => navigate("/app/arcade")} className="btn-ghost mb-6"><ArrowLeft className="w-4 h-4" /> Arcade</button>

      <div className="panel-glow p-6">
        <h1 className="font-head text-3xl font-black text-center mb-6" style={{ color: meta.accent }}>{meta.name}</h1>

        {phase === "intro" && (
          <div className="text-center py-10">
            <p className="text-muted-foreground mb-6">Ready to score some credits for your companion?</p>
            <button onClick={start} data-testid="start-game-btn" className="btn-primary text-base px-8 py-4">Start Game</button>
          </div>
        )}

        {phase === "playing" && <GameComp key={key} onEnd={handleEnd} />}

        {phase === "done" && (
          <div className="text-center py-6" data-testid="game-result">
            <Trophy className="w-14 h-14 mx-auto text-secondary mb-3" />
            <div className="label-cap">Final Score</div>
            <div className="font-head text-5xl font-black neon-text mb-6" style={{ color: meta.accent }}>{result?.score ?? 0}</div>
            <div className="flex justify-center gap-4 mb-6">
              <div className="panel px-5 py-3"><Coins className="w-5 h-5 text-secondary mx-auto mb-1" /><div className="font-bold">+{result?.credits_earned ?? 0}</div><div className="label-cap">credits</div></div>
              {result?.high_score != null && <div className="panel px-5 py-3"><Trophy className="w-5 h-5 text-accent mx-auto mb-1" /><div className="font-bold">{result.high_score}</div><div className="label-cap">best</div></div>}
            </div>
            <div className="flex gap-3 justify-center">
              <button onClick={start} data-testid="replay-btn" className="btn-primary"><RotateCcw className="w-4 h-4" /> Play again</button>
              <button onClick={() => navigate("/app/arcade")} className="btn-ghost">Back to arcade</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
